import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSurveyReopen } from "@/queries/surveys/use-surveys.query.ts";
import { ApiSurvey } from "@/types/survey.types.ts";
import { Button, Modal, ModalProps, Stack, Text, useModals } from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const ReopenSchemaObject = yup.object({
  date_ends_at: yup.date().defined().nullable(),
});

type ReopenSchema = yup.InferType<typeof ReopenSchemaObject>;

export interface SurveyReopenModalProps extends ModalProps {
  survey: ApiSurvey;
  onSuccess?: () => void;
}

export function SurveyReopenModal({ survey, onSuccess, ...props }: SurveyReopenModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const reopenSurvey = useSurveyReopen();

  const today = new Date();
  const minDate =
    survey.date_starts_at && survey.date_starts_at > today ? survey.date_starts_at : today;

  const defaultDateEndsAt = (() => {
    if (!survey.date_ends_at) return null;
    const nextWeekDate = new Date();
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);
    return nextWeekDate > minDate ? nextWeekDate : minDate;
  })();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReopenSchema>({
    resolver: yupResolver(ReopenSchemaObject),
    defaultValues: { date_ends_at: defaultDateEndsAt },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await reopenSurvey.mutateAsync({
        id: survey.id,
        payload: { date_ends_at: data.date_ends_at },
      });
      onSuccess?.();
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("surveys:reopen_survey_question")}
      withCloser
      actions={
        <Stack gap={"smd"} direction={"horizontal"} justify={"between"}>
          <ServerErrorField errors={errors} />
          <Button
            className={"w-full"}
            type={"submit"}
            size={"lg"}
            onClick={onSubmit}
            isLoading={reopenSurvey.isPending}
          >
            {t("surveys:reopen_survey")}
          </Button>
          <Button onClick={() => close(id)} variant={"secondary"} size={"lg"} className={"w-full"}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <Text className={"text-sm"}>{t("surveys:reopen_survey_confirm")}</Text>
        <Controller
          control={control}
          name="date_ends_at"
          render={({ field: { value, onChange } }) => (
            <Datepicker
              value={value}
              onChange={onChange}
              label={t("surveys:review_date_ends_at")}
              isClearable={true}
              minDate={minDate}
              error={errors.date_ends_at?.message}
            />
          )}
        />
      </Stack>
    </Modal>
  );
}
